// useIsModuleVisibleOrEnabled.js
import {DEFAULT_WORKSPACE, REPORT_MODULES_CATEGORIES, REPORT_MODULES_IDS} from '../../../../const';
import useBudget from '../../../providers/useBudget';
import usePayment from '../../../providers/usePayment';
import useProfile from '../../../providers/useProfile';
import useProjects from '../../../providers/useProjects';
import useReport from '../../../providers/useReport';
import useReportSummary from '../../../providers/useReportSummary';
import useReportViews from '../../../providers/useReportViews';
import useWorkspaces from '../../../providers/useWorkspaces';

export function useIsModuleVisibleOrEnabled(options = {}) {
  const {
    visibilityOverride // optional function
  } = options;

  const {canRefreshReport, reports = [], selectedReport, selectedProject: project, isDesktopLayout, isReportEditable} = useReport();
  const {currentProjectBudgets: budgets, spreadsheetDataLoaded, isExportingBudgetToFile, isDeletingBudget} = useBudget();
  const {profile} = useProfile();
  const {workspaces = [], isUserInWorkspace, isUserOwnerOfWorkspace} = useWorkspaces();
  const {currentReportPageSummary} = useReportSummary();
  const {CONNECTORS_HAVING_IMPORT_DATA_BUTTON} = useProjects();
  const {isGuest} = usePayment();
  const {reportViews, reportViewsLoading, reportViewInitialState, isReportPristine, reportViewsFeaturesEnabled} = useReportViews();

  const selectedReportId = selectedReport?.config?.id;
  const currentReport = reports.find(r => r.report_id === selectedReportId) || {};

  const isEditReportEnabled = () => {
    const reportWorkspace = workspaces.find(w => (w.reports || []).some(r => r.report_id === selectedReportId));
    return isReportEditable && isUserInWorkspace(profile?.id, reportWorkspace) && isDesktopLayout;
  };

  const defaultVisibility = module => {
    // console.log('defaultVisibility of module:  ', module);
    // console.log((currentReport?.forms || []).includes(module.formId));
    return (currentReport?.forms || []).includes(module.formId);
  };

  // eslint-disable-next-line complexity
  const isModuleVisible = module => {
    switch (module.id) {
      case REPORT_MODULES_IDS.EDIT_REPORT:
        return isEditReportEnabled();

      case REPORT_MODULES_IDS.DELETE_REPORT:
        return !isGuest && currentReport.isCustomModel;

      case REPORT_MODULES_IDS.EXPORT_REPORT:
      case REPORT_MODULES_IDS.REFRESH_REPORT:
      case REPORT_MODULES_IDS.SELECT_VIEW:
      case REPORT_MODULES_IDS.SAVE_VIEW:
      case REPORT_MODULES_IDS.RESET_VIEW:
      case REPORT_MODULES_IDS.CREATE_REPORT:
        return true;

      case REPORT_MODULES_IDS.UPLOAD_DATA:
        return CONNECTORS_HAVING_IMPORT_DATA_BUTTON.includes(project?.connector);

      case REPORT_MODULES_IDS.EDIT_BUDGET:
      case REPORT_MODULES_IDS.DELETE_BUDGET:
      case REPORT_MODULES_IDS.IMPORT_BUDGET:
      case REPORT_MODULES_IDS.EXPORT_BUDGET:
        return budgets.length > 0 && spreadsheetDataLoaded;

      default:
        if (visibilityOverride) {
          return visibilityOverride({module, currentReport});
        }
        return defaultVisibility(module);
    }
  };

  const isCreateReportModuleEnabled = () => {
    const userWorkspaces = workspaces.filter(w => w.workspace_id !== DEFAULT_WORKSPACE.workspace_id);
    if (userWorkspaces.length === 0) return false;
    if (userWorkspaces.length === 1) return isUserOwnerOfWorkspace(profile.id, userWorkspaces[0]);
    return true;
  };

  // eslint-disable-next-line complexity
  const isModuleEnabled = module => {
    if (module.requiresSirenSelected && !project) return false;

    const moduleCategoryRequiresOwnership = Object.values(REPORT_MODULES_CATEGORIES).find(entry => entry.modules.find(m => m.id === module.id))?.requiresProjectOwnership;
    if (module.requiresSirenSelected && project && !project.is_owner && moduleCategoryRequiresOwnership) return false;

    if (module.id === REPORT_MODULES_IDS.CREATE_REPORT) return isCreateReportModuleEnabled();
    if (module.id === REPORT_MODULES_IDS.UPDATE_REPORT_SUMMARY) return currentReportPageSummary !== null;
    if (module.id === REPORT_MODULES_IDS.REFRESH_REPORT) return canRefreshReport;

    if (module.id === REPORT_MODULES_IDS.SELECT_VIEW) {
      const userCreatedViews = reportViews.filter(rv => rv.is_visible);
      return userCreatedViews.length > 0 && !reportViewsLoading && reportViewsFeaturesEnabled;
    }

    if (module.id === REPORT_MODULES_IDS.RESET_VIEW) {
      return !reportViewInitialState || !isReportPristine;
    }

    if (module.id === REPORT_MODULES_IDS.EXPORT_BUDGET) {
      return !isExportingBudgetToFile && spreadsheetDataLoaded;
    }

    if (module.id === REPORT_MODULES_IDS.DELETE_BUDGET) {
      return !isDeletingBudget && spreadsheetDataLoaded;
    }

    const budgetModules = [REPORT_MODULES_IDS.CREATE_BUDGET, REPORT_MODULES_IDS.EDIT_BUDGET, REPORT_MODULES_IDS.IMPORT_BUDGET];

    if (budgetModules.includes(module.id)) {
      return spreadsheetDataLoaded;
    }

    return true;
  };

  const getNumberOfVisibleModulesCategories = () => {
    let total = 0;
    Object.values(REPORT_MODULES_CATEGORIES).forEach(c => {
      if (c.modules.some(m => isModuleVisible(m))) {
        total += 1;
      }
    });
    return total;
  };

  return {
    isModuleVisible,
    isModuleEnabled,
    getNumberOfVisibleModulesCategories
  };
}
